import React from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { ensureCurrentUser } from '../../util/data';
import { normalizeLanguage } from '../../util/locale';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { sendContactRequest } from '../../util/api';
import { parse, stringify } from '../../util/urlHelpers';
import { emailFormatValid, maxLength, required } from '../../util/validators';

import {
  Button,
  FieldSelect,
  FieldTextInput,
  H1,
  LayoutSingleColumn,
  NamedLink,
  Page,
} from '../../components';

import FooterContainer from '../FooterContainer/FooterContainer';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import css from './ContactPage.module.css';

const NAME_MAX_LENGTH = 120;
const PHONE_MAX_LENGTH = 80;
const ORDER_REFERENCE_MAX_LENGTH = 160;
const MESSAGE_MAX_LENGTH = 5000;

const CONTACT_CARD_TYPES = [
  {
    id: 'generalInquiry',
    titleId: 'ContactPage.type.general.title',
    textId: 'ContactPage.type.general.text',
  },
  {
    id: 'newRacketOffer',
    titleId: 'ContactPage.type.newRacketOffer.title',
    textId: 'ContactPage.type.newRacketOffer.text',
  },
];

const CONTACT_TOPICS = [
  { id: 'generalInquiry', labelId: 'ContactPage.topic.generalInquiry' },
  { id: 'newRacketOffer', labelId: 'ContactPage.topic.newRacketOffer' },
  { id: 'orderProblem', labelId: 'ContactPage.topic.orderProblem' },
  { id: 'buyerProtection', labelId: 'ContactPage.topic.buyerProtection' },
  { id: 'selling', labelId: 'ContactPage.topic.selling' },
  { id: 'payment', labelId: 'ContactPage.topic.payment' },
  { id: 'shipping', labelId: 'ContactPage.topic.shipping' },
  { id: 'technicalProblem', labelId: 'ContactPage.topic.technicalProblem' },
  { id: 'other', labelId: 'ContactPage.topic.other' },
];

const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), undefined);

const hasCardType = type => CONTACT_CARD_TYPES.some(option => option.id === type);

const validTopic = topic =>
  CONTACT_TOPICS.some(option => option.id === topic) ? topic : CONTACT_TOPICS[0].id;

const getInitialType = location => {
  const queryType = parse(location?.search || '')?.type;
  return queryType ? (hasCardType(queryType) ? queryType : '') : CONTACT_CARD_TYPES[0].id;
};

const getInitialTopic = location => validTopic(parse(location?.search || '')?.type);

const getMessagePlaceholderId = topic => {
  return topic === 'newRacketOffer'
    ? 'ContactPage.form.messagePlaceholder.newRacketOffer'
    : topic === 'orderProblem'
    ? 'ContactPage.form.messagePlaceholder.orderProblem'
    : 'ContactPage.form.messagePlaceholder.general';
};

const RequiredLabel = props => {
  const { id, intl } = props;
  return (
    <>
      {intl.formatMessage({ id })}
      <span className={css.requiredMark} aria-hidden="true">
        *
      </span>
    </>
  );
};

const ContactTypeSelector = props => {
  const { onTypeChange, onTopicChange } = props;

  return (
    <Field name="type">
      {({ input }) => (
        <div className={css.typeGrid} role="group">
          {CONTACT_CARD_TYPES.map(option => {
            const selected = input.value === option.id;
            const buttonClasses = classNames(css.typeCard, {
              [css.typeCardSelected]: selected,
            });
            return (
              <button
                key={option.id}
                type="button"
                className={buttonClasses}
                aria-pressed={selected}
                onClick={() => {
                  input.onChange(option.id);
                  onTopicChange(option.id);
                  onTypeChange(option.id);
                }}
              >
                <span className={css.typeTitle}>
                  <FormattedMessage id={option.titleId} />
                </span>
                <span className={css.typeText}>
                  <FormattedMessage id={option.textId} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Field>
  );
};

/**
 * Public contact page with inquiry routing for general questions and new racket offer requests.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
export const ContactPageComponent = props => {
  const { currentUser, scrollingDisabled } = props;
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const [submitStatus, setSubmitStatus] = React.useState(null);
  const marketplaceName = config.marketplaceName;
  const initialType = getInitialType(location);
  const initialTopic = getInitialTopic(location);
  const user = ensureCurrentUser(currentUser);
  const userProfile = user.attributes.profile || {};
  const userProtectedData = userProfile.protectedData || {};
  const title = intl.formatMessage({ id: 'ContactPage.schemaTitle' }, { marketplaceName });
  const description = intl.formatMessage(
    { id: 'ContactPage.schemaDescription' },
    { marketplaceName }
  );

  const updateTypeInUrl = type => {
    const queryParams = parse(location?.search || '');
    const search = stringify({ ...queryParams, type });
    history.replace({ pathname: location.pathname, search: `?${search}` });
  };

  const handleSubmit = async (values, form) => {
    setSubmitStatus(null);
    try {
      await sendContactRequest({
        type: values.type,
        topic: values.topic,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
        orderReference: values.orderReference,
        message: values.message,
        company: values.company,
        locale: intl.locale || config.localization?.locale,
        currentUrl: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
        userId: user.id?.uuid,
      });
      setSubmitStatus('success');
      form.restart({ type: values.type, topic: values.topic });
    } catch (e) {
      setSubmitStatus('error');
    }
  };

  const requiredMessage = intl.formatMessage({ id: 'ContactPage.form.required' });
  const emailRequiredMessage = intl.formatMessage({ id: 'ContactPage.form.emailRequired' });
  const emailInvalidMessage = intl.formatMessage({ id: 'ContactPage.form.emailInvalid' });
  const topicInvalidMessage = intl.formatMessage({ id: 'ContactPage.form.topicInvalid' });
  const maxLengthMessage = max => intl.formatMessage({ id: 'ContactPage.form.maxLength' }, { max });
  const validateTopic = value =>
    CONTACT_TOPICS.some(option => option.id === value) ? undefined : topicInvalidMessage;
  const privacyRouteName =
    normalizeLanguage(intl.locale) === 'de' ? 'PrivacyPolicyPageGerman' : 'PrivacyPolicyPage';
  const privacyLink = (
    <NamedLink name={privacyRouteName} className={css.privacyLink}>
      <FormattedMessage id="ContactPage.form.privacyLink" />
    </NamedLink>
  );
  const directEmail = <span className={css.emailFallback}>info@racketport.com</span>;
  const initialValues = {
    type: initialType,
    topic: initialTopic,
    firstName: userProfile.firstName || '',
    lastName: userProfile.lastName || '',
    email: user.attributes.email || '',
    phone: userProtectedData.phoneNumber || '',
  };

  return (
    <Page title={title} description={description} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <section className={css.hero}>
            <div className={css.heroInner}>
              <p className={css.eyebrow}>
                <FormattedMessage id="ContactPage.hero.eyebrow" />
              </p>
              <H1 rootClassName={css.heroTitle}>
                <FormattedMessage id="ContactPage.hero.title" />
              </H1>
              <p className={css.heroText}>
                <FormattedMessage id="ContactPage.hero.text" values={{ marketplaceName }} />
              </p>
            </div>
          </section>

          <section className={css.contactSection}>
            <FinalForm
              initialValues={initialValues}
              onSubmit={handleSubmit}
              render={({ form, handleSubmit, submitting, values }) => {
                const messagePlaceholderId = getMessagePlaceholderId(values.topic);
                return (
                  <form className={css.form} onSubmit={handleSubmit}>
                    <div className={css.formIntro}>
                      <h2 className={css.formTitle}>
                        <FormattedMessage id="ContactPage.form.title" />
                      </h2>
                      <p className={css.formText}>
                        <FormattedMessage id="ContactPage.form.text" />
                      </p>
                    </div>
                    <ContactTypeSelector
                      onTypeChange={updateTypeInUrl}
                      onTopicChange={topic => form.change('topic', topic)}
                    />
                    <FieldSelect
                      id="contact-topic"
                      name="topic"
                      className={css.topicField}
                      selectClassName={css.topicSelect}
                      label={<RequiredLabel id="ContactPage.form.topic" intl={intl} />}
                      aria-required="true"
                      validate={composeValidators(required(requiredMessage), validateTopic)}
                      onChange={topic => {
                        form.change('type', hasCardType(topic) ? topic : '');
                        updateTypeInUrl(topic);
                      }}
                    >
                      {CONTACT_TOPICS.map(option => (
                        <option key={option.id} value={option.id}>
                          {intl.formatMessage({ id: option.labelId })}
                        </option>
                      ))}
                    </FieldSelect>
                    <div className={css.fieldsGrid}>
                      <FieldTextInput
                        id="contact-firstName"
                        name="firstName"
                        type="text"
                        label={<RequiredLabel id="ContactPage.form.firstName" intl={intl} />}
                        aria-required="true"
                        validate={composeValidators(
                          required(requiredMessage),
                          maxLength(maxLengthMessage(NAME_MAX_LENGTH), NAME_MAX_LENGTH)
                        )}
                      />
                      <FieldTextInput
                        id="contact-lastName"
                        name="lastName"
                        type="text"
                        label={<RequiredLabel id="ContactPage.form.lastName" intl={intl} />}
                        aria-required="true"
                        validate={composeValidators(
                          required(requiredMessage),
                          maxLength(maxLengthMessage(NAME_MAX_LENGTH), NAME_MAX_LENGTH)
                        )}
                      />
                      <FieldTextInput
                        id="contact-email"
                        name="email"
                        type="email"
                        label={<RequiredLabel id="ContactPage.form.email" intl={intl} />}
                        aria-required="true"
                        validate={composeValidators(
                          required(emailRequiredMessage),
                          emailFormatValid(emailInvalidMessage)
                        )}
                      />
                      <FieldTextInput
                        id="contact-phone"
                        name="phone"
                        type="tel"
                        label={intl.formatMessage({ id: 'ContactPage.form.phoneOptional' })}
                        validate={maxLength(maxLengthMessage(PHONE_MAX_LENGTH), PHONE_MAX_LENGTH)}
                      />
                      <FieldTextInput
                        id="contact-orderReference"
                        name="orderReference"
                        type="text"
                        label={intl.formatMessage({
                          id: 'ContactPage.form.orderReferenceOptional',
                        })}
                        helpText={intl.formatMessage({
                          id: 'ContactPage.form.orderReferenceHelp',
                        })}
                        validate={maxLength(
                          maxLengthMessage(ORDER_REFERENCE_MAX_LENGTH),
                          ORDER_REFERENCE_MAX_LENGTH
                        )}
                      />
                    </div>
                    <FieldTextInput
                      id="contact-message"
                      name="message"
                      type="textarea"
                      className={css.messageField}
                      label={<RequiredLabel id="ContactPage.form.message" intl={intl} />}
                      aria-required="true"
                      placeholder={intl.formatMessage({ id: messagePlaceholderId })}
                      validate={composeValidators(
                        required(requiredMessage),
                        maxLength(maxLengthMessage(MESSAGE_MAX_LENGTH), MESSAGE_MAX_LENGTH)
                      )}
                    />
                    <Field name="company">
                      {({ input }) => (
                        <div className={css.honeypot} aria-hidden="true">
                          <label htmlFor="contact-company">Company</label>
                          <input
                            id="contact-company"
                            type="text"
                            className={css.honeypotInput}
                            tabIndex="-1"
                            autoComplete="off"
                            {...input}
                          />
                        </div>
                      )}
                    </Field>
                    <div className={css.submitRow}>
                      <Button type="submit" className={css.submitButton} disabled={submitting}>
                        {submitting ? (
                          <FormattedMessage id="ContactPage.form.submitting" />
                        ) : (
                          <FormattedMessage id="ContactPage.form.submit" />
                        )}
                      </Button>
                    </div>
                    {submitStatus === 'success' ? (
                      <p className={css.successMessage} role="status">
                        <FormattedMessage id="ContactPage.form.success" />
                      </p>
                    ) : null}
                    {submitStatus === 'error' ? (
                      <p className={css.errorMessage} role="alert">
                        <FormattedMessage
                          id="ContactPage.form.error"
                          values={{ email: directEmail }}
                        />
                      </p>
                    ) : null}
                    <p className={css.privacyNotice}>
                      <FormattedMessage
                        id="ContactPage.form.privacyNotice"
                        values={{ privacyLink }}
                      />
                    </p>
                  </form>
                );
              }}
            />
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    currentUser: state.user.currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const ContactPage = compose(connect(mapStateToProps))(ContactPageComponent);

export default ContactPage;
